import { useAuftragCtx } from './AuftragLayout.jsx';
import { useZeitplan } from '../hooks/data.js';
import { dayLabel, dateTimeLabel, isoDate, kw } from '../lib/format.js';

const DAY = 86_400_000;
const t = (iso) => new Date(`${iso}T12:00:00`).getTime();

export function Plan() {
  const { nr } = useAuftragCtx();
  const plan = useZeitplan(nr);

  if (plan.isLoading) return <div className="skeleton" style={{ height: 300 }} />;
  if (plan.isError) return <div className="empty" role="alert">{plan.error.message}</div>;

  const { phasen = [], termine = [], geaendertVon, geaendertAm } = plan.data ?? {};
  const today = isoDate();

  if (!phasen.length && !termine.length) {
    return <div className="empty">Für diesen Auftrag ist noch kein Zeitplan erfasst. Die Projektleitung pflegt Phasen und Termine.</div>;
  }

  const start = Math.min(...phasen.map((p) => t(p.start)), t(today));
  const end = Math.max(...phasen.map((p) => t(p.ende)), t(today)) + DAY;
  const span = end - start || DAY;
  const pct = (ms) => `${(((ms - start) / span) * 100).toFixed(2)}%`;

  const weeks = [];
  for (let d = start; d < end; d += 7 * DAY) weeks.push(kw(isoDate(new Date(d))));

  return (
    <div className="pl">
      <section className="card list">
        <h2 className="h2" style={{ margin: '10px 0 4px' }}>Termine</h2>
        {termine.length === 0 && <p className="sub" style={{ padding: '6px 0 10px' }}>Keine Termine erfasst.</p>}
        {termine.map((x, i) => (
          <div key={i} className="listrow" style={{ alignItems: 'flex-start', opacity: x.datum < today ? 0.55 : 1 }}>
            <span className="stack" style={{ width: 92, flexShrink: 0, gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: x.datum === today ? 'var(--stop)' : 'var(--ink)' }}>{dayLabel(x.datum)}</span>
              <span className="sub tn" style={{ fontSize: 12 }}>{x.von}{x.bis ? `–${x.bis}` : ''}</span>
            </span>
            <span className="stack" style={{ gap: 2 }}>
              <span style={{ fontSize: 15, fontWeight: 800 }}>{x.titel}</span>
              {x.wer && <span className="sub">{x.wer}</span>}
            </span>
          </div>
        ))}
      </section>

      <section className="card" style={{ gap: 14 }}>
        <div className="between">
          <h2 className="h2">Bauzeitplan</h2>
          <span className="sub" style={{ fontWeight: 700 }}>KW {weeks[0]}–{weeks[weeks.length - 1]}</span>
        </div>
        <div className="row" style={{ gap: 0, paddingBottom: 6, borderBottom: '1px solid var(--line-2)' }} aria-hidden="true">
          {weeks.map((w) => (
            <span key={w} className="tn" style={{ flex: 1, textAlign: 'center', fontSize: 11, fontWeight: w === kw(today) ? 800 : 600, color: w === kw(today) ? 'var(--stop)' : 'var(--muted-2)' }}>{w}</span>
          ))}
        </div>
        {phasen.map((p, i) => {
          const done = p.fortschritt >= 100;
          const active = !done && p.start <= today;
          return (
            <div key={i} className="stack" style={{ gap: 6 }}>
              <div className="between">
                <span style={{ fontSize: 14, fontWeight: 800, minWidth: 0 }}>{p.titel}</span>
                <span className={`pill ${done ? 'grey' : active ? 'ok' : 'info'}`}>{done ? 'Erledigt' : active ? `Läuft · ${p.fortschritt} %` : 'Geplant'}</span>
              </div>
              <div className="track" role="img" aria-label={`${p.titel}: ${dayLabel(p.start)} bis ${dayLabel(p.ende)}`}>
                <div className="seg" style={{ left: pct(t(p.start)), width: `${(((t(p.ende) + DAY - t(p.start)) / span) * 100).toFixed(2)}%`, background: done ? '#9DB5AC' : active ? 'var(--accent)' : '#C9D3CF' }} />
                <div className="today" style={{ left: pct(t(today) + DAY / 2) }} />
              </div>
              <span className="sub tn" style={{ fontSize: 12 }}>{dayLabel(p.start)} – {dayLabel(p.ende)} · KW {kw(p.start)}{kw(p.ende) !== kw(p.start) ? `–${kw(p.ende)}` : ''}</span>
            </div>
          );
        })}
        <div className="between">
          <span className="row sub" style={{ fontSize: 12, gap: 8 }}><span style={{ width: 12, height: 2, background: 'var(--stop)' }} />Heute</span>
          {geaendertVon && <span className="sub" style={{ fontSize: 12 }}>Stand: {geaendertVon}, {dateTimeLabel(geaendertAm)}</span>}
        </div>
      </section>
    </div>
  );
}
